import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * COLUMN LAYOUT: WIDTH, ORDER, VISIBILITY AND PINNING, OWNED BY THE READER.
 *
 * The manager's ask, Sept 2026: "as powerful and user-friendly as Power BI".
 * Sorting and extracts already existed; what a reader could not do was change
 * the SHAPE of the grid — widen a column, move it, put it away, or keep it on
 * screen while scrolling across thirty-four others.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS NOT
 *
 * It is not a filter, and it is not a permission. Hiding a column here is a
 * VIEW preference: the figure was fetched, audited and sent, and the reader has
 * chosen not to look at it. What a person may see at all is decided by column
 * governance in the database, and nothing in this file can widen that. The two
 * must never be confused in the UI either, which is why the panel below says
 * "Shown in this grid" rather than anything resembling access.
 *
 * ---------------------------------------------------------------------------
 * THREE RULES THAT SHAPED THE IMPLEMENTATION
 *
 * 1. A HEADER IS STILL NEVER TRUNCATED, AND NEITHER IS A FIGURE. The grid's
 *    older contract — columns are sized from their header text, the viewport
 *    gives — survives: a drag cannot take a column below its own header's
 *    width. Since 4 Oct 2026 it cannot take one below its widest FIGURE either
 *    (`minWidthOf`), and a stored width is a FLOOR for a figure column, never a
 *    ceiling: the grids no longer cap a number cell at the reader's width.
 *
 *    The sales manager's screenshot is why. A reader narrowed the Explorer's
 *    sales columns while they held dollars ("$79,869,869"); the width was
 *    remembered; in local currency the same cells read "SAR 79,869,869", wider
 *    than the stored width, and each figure ran into its neighbour -- two
 *    numbers printed over each other, which is worse than either being absent.
 *    A clipped number is a wrong number, and so is an overlapped one.
 *
 *    FREE TEXT may still clip once narrowed -- a customer name, a reason --
 *    which is the reader's explicit choice and keeps the full value one hover
 *    away in the title attribute, as `clamp` always has.
 *
 * 2. PINNING MOVES THE COLUMN TO THE EDGE, the way Power BI and every grid the
 *    manager compared us to behave. That is why order and pinning are resolved
 *    together below rather than being two independent lists.
 *
 * 3. OFFSETS ARE MEASURED, NOT ASSUMED. Two pinned columns need the second one
 *    offset by the first one's real width, and the old grid ducked this by
 *    allowing exactly one per side. Widths here come from the DOM after layout,
 *    so a resized, re-ordered, or font-scaled grid still pins correctly.
 *
 * ---------------------------------------------------------------------------
 * THE LAYOUT LIVES FOR THE SESSION, ON PURPOSE
 *
 * Kept in a module-level store keyed by grid, so moving between tabs and back
 * finds the grid as you left it, and lost on reload. Durable storage — browser
 * or database, per user — is a decision the owner parked on 18 Sep 2026, and
 * `snapshot()` / `restore()` below are the two functions that will attach it
 * when it is taken. Nothing else needs to change for that.
 */

export type Pin = 'left' | 'right' | null

export interface GridLayout {
  /** Every key, in display order, before pinning is applied. */
  order: string[]
  hidden: string[]
  /** Only columns the reader has actually dragged. Everything else sizes itself. */
  widths: Record<string, number>
  pinned: Record<string, 'left' | 'right'>
  /* Key to header text. The extract builders describe their columns by HEADER
     and carry no key — they are a different array from the on-screen one — so
     this is what lets applyLayout() match the two up. */
  headers: Record<string, string>
}

/** What a grid needs to know about a column to lay it out. */
export interface LayoutColumn {
  key: string
  header: string
  stickyLeft?: boolean
  stickyRight?: boolean
  /**
   * Start hidden — the grid's own default, not the reader's choice.
   *
   * For a grid that ships a sensible subset of a long column list: Invoice
   * Lines shows twelve of twenty-five until somebody asks for the rest. Before
   * this, that grid kept a SECOND control (a Key / All 25 toggle, itself
   * labelled "Columns") which swapped the column array underneath the layout —
   * changing the key set, which this hook reads as a different grid and
   * resets. One list with a default is the same feature without the collision.
   */
  hiddenByDefault?: boolean
}

const store = new Map<string, GridLayout>()

/** The layout of one grid right now, or null. Used by the extract builders. */
export function snapshot(gridId: string | undefined): GridLayout | null {
  return gridId ? (store.get(gridId) ?? null) : null
}

/* ===========================================================================
   DURABLE STORAGE — the decision parked on 18 Sep 2026, taken on 24 Sep.

   Everything below is the attachment this file always said it would need, and
   it is deliberately the whole of it: the layouts are stored by
   web/src/components/gridLayoutStore.ts, which owns the debounce and the API
   calls, and this file knows nothing about either.

   THE SINK IS INJECTED, NEVER IMPORTED. gridLayout.tsx imports nothing but
   React today and is itself imported by ui.tsx and DataGrid.tsx; importing an
   API client from here would close a cycle that resolves to `undefined` at
   point of use rather than failing at build. DevModeContext.tsx exists for the
   same reason.
   =========================================================================== */

/** What durable storage has to offer. See gridLayoutStore for the only one. */
export interface GridLayoutSink {
  /** A grid changed. Debounce before writing — this fires on every drag frame. */
  onChange(gridId: string, layout: GridLayout): void
  /** Reset was pressed: forget it, so the grid ships its own defaults for ever. */
  onForget(gridId: string): void
}

let sink: GridLayoutSink | null = null

/** Attach durable storage, or pass null to detach. Called once, from App. */
export function configureGridLayoutStore(next: GridLayoutSink | null): void {
  sink = next
}

/* Grids already on screen when a stored layout arrives. restore() writes the
   module store, which no component is watching — without this the layout would
   land and nothing would redraw until the next unrelated render. */
const watchers = new Set<() => void>()

function subscribe(fn: () => void): () => void {
  watchers.add(fn)
  return () => {
    watchers.delete(fn)
  }
}

/**
 * Put a layout back, from durable storage.
 *
 * Every grid re-renders, not only the one named: a report opens several and the
 * store is loaded in one call, so the alternative is tracking which components
 * care about which id to save a handful of renders of tables that are already
 * mounted.
 */
export function restore(gridId: string, layout: GridLayout): void {
  store.set(gridId, layout)
  for (const w of watchers) w()
}

/**
 * Empty the store completely.
 *
 * FOR AN IDENTITY CHANGE. The store is module-level, so it survives a Login As
 * switch — and an administrator who has arranged their own columns would
 * otherwise carry that arrangement into the screen of the person they are
 * acting as, then save it there. MaterialPlanningPage makes the same argument
 * about its option lists: what is in state belongs to the previous identity.
 */
export function forgetAllLayouts(): void {
  store.clear()
  for (const w of watchers) w()
}

/**
 * Reorder and filter a list of columns to match what the grid is showing.
 *
 * FOR THE EXTRACT BUILDERS, and it is the reason this is exported at all. The
 * house rule is that an extract is exactly the screen; a reader who hides six
 * columns and presses Excel must not get them back in the file. Export column
 * lists are separate arrays from the on-screen ones (they carry `cell:` rather
 * than `render:`), so they are matched by key.
 *
 * A column the layout has never heard of is kept, in its original position
 * relative to the others: an extract may legitimately carry a column the grid
 * does not show, and dropping it silently would be the worse failure.
 */
export function applyLayout<T extends { key?: string; header?: string }>(
  gridId: string | undefined,
  columns: T[],
): T[] {
  const layout = snapshot(gridId)
  if (!layout) return columns

  /* Matched by key where the caller has one and by header text otherwise, so
     the same function serves the on-screen columns and the extract's. */
  const rank = new Map<string, number>()
  layout.order.forEach((k, i) => {
    rank.set(k, i)
    const h = layout.headers[k]
    if (h !== undefined && !rank.has(h)) rank.set(h, i)
  })
  const hidden = new Set<string>()
  for (const k of layout.hidden) {
    hidden.add(k)
    const h = layout.headers[k]
    if (h !== undefined) hidden.add(h)
  }

  const rankOf = (c: T) => (c.key !== undefined ? rank.get(c.key) : undefined) ?? (c.header !== undefined ? rank.get(c.header) : undefined)
  const isHidden = (c: T) => (c.key !== undefined && hidden.has(c.key)) || (c.header !== undefined && hidden.has(c.header))

  return columns
    .map((c, i) => ({ c, i, r: rankOf(c) }))
    .filter((e) => !isHidden(e.c))
    .sort((a, b) => {
      /* A column the layout never knew — an extract-only field such as the raw
         Unit beside a formatted quantity — keeps its original place rather than
         being dropped or flung to one end. */
      if (a.r === undefined || b.r === undefined) return a.i - b.i
      return a.r - b.r
    })
    .map((e) => e.c)
}

function initial<T extends LayoutColumn>(columns: T[]): GridLayout {
  const pinned: Record<string, 'left' | 'right'> = {}
  const headers: Record<string, string> = {}
  const hidden: string[] = []
  for (const c of columns) {
    headers[c.key] = c.header
    if (c.hiddenByDefault) hidden.push(c.key)
    if (c.stickyLeft) pinned[c.key] = 'left'
    else if (c.stickyRight) pinned[c.key] = 'right'
  }
  return { order: columns.map((c) => c.key), hidden, widths: {}, pinned, headers }
}

/**
 * A stored layout, made to fit the grid as it is now.
 *
 * Returns `stored` UNCHANGED — the same reference — when nothing needs doing,
 * which is the overwhelmingly common case and is what lets the caller tell
 * "reconciled" from "untouched" without comparing.
 *
 * The three moves, in the reader's favour wherever the answer is a judgement:
 *
 *   a stored key the grid does not declare     kept, DORMANT: in the order, with
 *     right now                                its width, pinning and hidden
 *                                              state, drawn nowhere until the
 *                                              grid declares it again
 *   a live key the stored order never knew     inserted where the grid declares
 *                                              it, taking the grid's own
 *                                              hiddenByDefault and pinning
 *   everything both sides know                 kept, in the reader's order,
 *                                              with their widths and pinning
 *
 * Only an empty intersection falls back to the grid's defaults. That is not a
 * changed grid, it is a different one.
 *
 * DORMANT, NOT DROPPED (4 Oct 2026). A grid's columns change under a reader
 * without the grid changing: Inventory Position draws no Product Name column
 * when grouped by company, a grant can come and go. Dropping what was not
 * declared meant a reader who widened Product Name, looked at the company view
 * and came back found it at its default width -- and their next drag saved the
 * loss for good. Measured in the demo that day: 268px back to 148px, and gone
 * after a reload. A key that never returns costs a few bytes in one row.
 */
function reconcile(stored: GridLayout, base: GridLayout): GridLayout {
  const live = new Set(base.order)
  const kept = stored.order.filter((k) => live.has(k))

  // Nothing in common: a different grid wearing the same id.
  if (kept.length === 0) return base

  const knew = new Set(stored.order)
  const added = base.order.filter((k) => !knew.has(k))
  /* Headers always come from the live columns -- see the note on the field --
     and one key can change its header without the key set changing: the
     grouped column is "Business Unit" by unit and "Business Team" by team. */
  const headers = { ...stored.headers, ...base.headers }
  const sameHeaders = base.order.every((k) => stored.headers[k] === base.headers[k])
  if (added.length === 0) return sameHeaders ? stored : { ...stored, headers }

  /* New columns land where the GRID declares them, not at the end: a column
     added between two others belongs between them, and appending it would put
     every new column at the right-hand edge for everyone who has ever arranged
     that grid -- which is most of the people who would notice it. Placed after
     the nearest column the grid declares before it, so a dormant key between
     the two cannot shift it. */
  const order = stored.order.slice()
  for (const k of added) {
    const at = base.order.indexOf(k)
    const prev = base.order
      .slice(0, at)
      .reverse()
      .find((x) => order.includes(x))
    order.splice(prev === undefined ? 0 : order.indexOf(prev) + 1, 0, k)
  }

  // A new column takes the grid's own default, which is the whole of the
  // hidden-versus-hiddenByDefault question.
  const hidden = [...stored.hidden]
  for (const k of added) if (base.hidden.includes(k)) hidden.push(k)

  const pinned = { ...stored.pinned }
  for (const k of added) if (base.pinned[k]) pinned[k] = base.pinned[k]

  return { order, hidden, widths: { ...stored.widths }, pinned, headers }
}

/* A column's declared pinning is the DEFAULT, not a fixed fact: the grids that
   pin an identity column left and an action column right keep doing so until
   the reader says otherwise. Comparing against this is also what "is this
   layout dirty?" means, which is what decides whether Reset is offered. */
function isPristine(layout: GridLayout, base: GridLayout): boolean {
  /* Judged on the columns DRAWN. A dormant key's arrangement is real and kept,
     but it is not on this screen, and Reset offered over a grid that looks
     exactly as shipped reads as "something is off" when nothing visible is. */
  const live = new Set(base.order)
  const drawn = (keys: string[]) => keys.filter((k) => live.has(k))
  const pins = (p: GridLayout['pinned']) => base.order.filter((k) => p[k]).map((k) => `${k}:${p[k]}`).join()
  return (
    /* Against the grid's OWN default, not against "nothing hidden": a grid that
       ships with columns put away would otherwise offer Reset from the moment
       it loaded, which reads as "something is off" when nothing is. */
    drawn(layout.hidden).sort().join() === base.hidden.slice().sort().join() &&
    !Object.keys(layout.widths).some((k) => live.has(k)) &&
    drawn(layout.order).join() === base.order.join() &&
    pins(layout.pinned) === pins(base.pinned)
  )
}

export interface GridLayoutApi {
  /** Columns to render: visible, ordered, pinned columns moved to the edges. */
  columns: LayoutColumn[]
  /** All columns with their current state, for the panel. */
  all: { column: LayoutColumn; hidden: boolean; pinned: Pin }[]
  widthOf: (key: string) => number | undefined
  /** What the column measures on screen right now, set or not — a drag's start. */
  headerWidth: (key: string) => number
  /** Left/right inset for a pinned column, in px, from measured widths. */
  offsetOf: (key: string) => number
  pinOf: (key: string) => Pin
  /** Ref for each header cell, so widths can be measured for the offsets. */
  headerRef: (key: string) => (el: HTMLElement | null) => void
  resize: (key: string, width: number) => void
  autoFit: (key: string) => void
  move: (key: string, toIndex: number) => void
  /** Where a key sits in the full order, so a header drop can name its target. */
  indexOf: (key: string) => number
  toggle: (key: string) => void
  /** Hide exactly these keys — for a preset such as "the key columns only". */
  setHidden: (keys: string[]) => void
  /** Which keys are hidden right now, so a preset control can show its state. */
  hidden: string[]
  setPin: (key: string, pin: Pin) => void
  reset: () => void
  dirty: boolean
  hiddenCount: number
  /** Minimum a column may be dragged to: its own header, never less. */
  minWidthOf: (key: string) => number
}

/* Omit rather than intersect: `GridLayoutApi & { columns: T[] }` resolves the
   property to the BASE type, so the caller loses render(), align() and the rest
   of its own column shape. */
export function useGridLayout<T extends LayoutColumn>(
  gridId: string | undefined,
  columns: T[],
  /**
   * A fixed column that sits BEFORE these and is not one of them — the drill
   * handle on the aging matrix and the DSO grid.
   *
   * It is outside the layout on purpose: it carries the chevron and the stepped
   * indent, its header text changes as the reader drills (so it has no stable
   * name for an extract to match), and a grid of figures addressed to nothing
   * is what hiding it would produce. But it is sticky, so every reader-pinned
   * column has to start after its real width or the first one slides underneath
   * it. Measured from the element rather than assumed, because that width is a
   * min-width the content can exceed.
   */
  leadingRef?: { current: HTMLElement | null },
): Omit<GridLayoutApi, 'columns'> & { columns: T[] } {
  const base = initial(columns)
  const [, bump] = useState(0)
  const rerender = useCallback(() => bump((n) => n + 1), [])

  /* A grid whose columns no longer match the stored ones is RECONCILED, not
     discarded. It used to be discarded — any difference in the key set replaced
     the reader's whole arrangement with the grid's defaults, silently — and
     that was survivable while a layout died on reload anyway. It is not
     survivable now that layouts persist: the same check fires on a PERMISSION
     difference, so a reader who loses SLS_Mod would have lost their column
     order everywhere, for good, with nothing said.

     The rule: keep what both sides know, in the reader's order; keep what the
     grid does not declare right now, dormant, so it comes back as the reader
     left it; insert what the grid has gained at the position it declares it.
     Only an EMPTY intersection starts over, which is a genuinely different grid
     rather than a changed one.

     THIS IS ALSO WHAT RESOLVES THE `hidden` AMBIGUITY, without storing anything
     extra. `hidden` and `pinned` are seeded from the grid's own declarations in
     initial(), so a stored `hidden` cannot say whether the grid put a column
     away or the reader did. It does not need to: `order` is the record of which
     keys the stored layout knew about. A key in `order` takes the reader's
     choice; a key absent from it is new, and takes the grid's. */
  const known = gridId ? store.get(gridId) : undefined
  const reconciled = known ? reconcile(known, base) : base
  if (gridId && (!known || reconciled !== known)) store.set(gridId, reconciled)
  const layout = (gridId ? store.get(gridId)! : reconciled)

  /* Redraw when durable storage hands a layout back. It arrives after the grid
     has already drawn from `base`, and nothing is watching the module store.

     ABOVE EVERY EARLY RETURN, and this hook is the reason to check: the
     hooks-order gate exists because exactly this mistake shipped twice, on
     Material Planning and then on Sales. */
  useEffect(() => subscribe(rerender), [rerender])

  /**
   * `persist` is false for exactly one caller: Reset, which has already told
   * the sink to FORGET this grid. Without the flag the very next line would
   * store the defaults it just asked to have forgotten, and the row would come
   * back — frozen as the grid looked on the day the button was pressed.
   */
  const write = useCallback(
    (next: GridLayout, persist = true) => {
      if (gridId) {
        store.set(gridId, next)
        /* Fires on every drag frame; the sink debounces. Headers are dropped on
           the way out -- see the note on GridLayout.headers for why a stored
           copy of the on-screen text is a stale copy. */
        if (persist) sink?.onChange(gridId, next)
      }
      rerender()
    },
    [gridId, rerender],
  )

  const byKey = new Map(columns.map((c) => [c.key, c]))
  const hidden = new Set(layout.hidden)
  /* What the panel and the presets talk about: the hidden columns DRAWN here.
     A dormant one's hidden state is kept for when it comes back. */
  const hiddenHere = layout.hidden.filter((k) => byKey.has(k))
  const hiddenDormant = layout.hidden.filter((k) => !byKey.has(k))
  const ordered = layout.order.map((k) => byKey.get(k)).filter((c): c is T => !!c)
  const visible = ordered.filter((c) => !hidden.has(c.key))
  const pinLeft = visible.filter((c) => layout.pinned[c.key] === 'left')
  const pinRight = visible.filter((c) => layout.pinned[c.key] === 'right')
  const middle = visible.filter((c) => !layout.pinned[c.key])
  const shown = [...pinLeft, ...middle, ...pinRight]

  /* MEASURED, because a pinned column's inset is the sum of the real widths of
     the pinned columns before it — which changes with a resize, a font, or a
     reorder. Written to a ref and mirrored into state only when it actually
     differs, so measuring cannot loop. */
  const els = useRef(new Map<string, HTMLElement>())
  const [offsets, setOffsets] = useState<Record<string, number>>({})
  const offsetsNow = useRef(offsets)
  const headerRef = useCallback(
    (key: string) => (el: HTMLElement | null) => {
      if (el) els.current.set(key, el)
      else els.current.delete(key)
    },
    [],
  )

  /* Keyed by the pinned columns' keys, not by the arrays, which are new on every
     draw: a new `measure` each draw rebuilt the ResizeObserver below on every
     frame of a scrolling list (review of 19m). */
  const leftKeys = pinLeft.map((c) => c.key).join('|')
  const rightKeys = pinRight.map((c) => c.key).join('|')
  const measure = useCallback(() => {
    const next: Record<string, number> = {}
    let left = leadingRef?.current?.offsetWidth ?? 0
    for (const key of leftKeys ? leftKeys.split('|') : []) {
      next[key] = left
      left += els.current.get(key)?.offsetWidth ?? 0
    }
    let right = 0
    for (const key of rightKeys ? rightKeys.split('|').reverse() : []) {
      next[key] = right
      right += els.current.get(key)?.offsetWidth ?? 0
    }
    // Compared here, before React is asked (29 Oct 2026): this runs after every
    // draw, and an updater handing back the same state still made React draw the
    // whole grid again to find that out -- every frame twice on a scrolling list.
    const prev = offsetsNow.current
    const same =
      Object.keys(next).length === Object.keys(prev).length &&
      Object.entries(next).every(([k, v]) => prev[k] === v)
    if (same) return
    offsetsNow.current = next
    setOffsets(next)
  }, [leftKeys, rightKeys, leadingRef])

  useLayoutEffect(measure)
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    for (const el of els.current.values()) ro.observe(el)
    return () => ro.disconnect()
  }, [measure, shown.length])

  /* The floor for a drag: the header's own text plus the cell's padding, read
     off the element rather than guessed, so it holds at any type scale.

     THE LABEL MUST BE INLINE-BLOCK for this to mean anything. A block label
     fills whatever width the column currently has, so measuring it returns
     "however wide this column is right now" — a floor that follows the column
     down and makes narrowing impossible. Measured in the browser: a 130px
     column widened to 220px then dragged 600px left stopped at 234px. An
     inline-block shrinks to its text and keeps that width even when the column
     is narrower, which is exactly the intrinsic number wanted here. */
  const minWidthOf = useCallback((key: string) => {
    const el = els.current.get(key)
    if (!el) return 60
    const label = el.querySelector('[data-head-label]') as HTMLElement | null
    const s = getComputedStyle(el)
    const box =
      parseFloat(s.paddingLeft) + parseFloat(s.paddingRight) +
      parseFloat(s.borderLeftWidth) + parseFloat(s.borderRightWidth)
    /* The handle sits inside the cell's right padding; without its width the
       last glyph ends up underneath it. */
    const header = Math.ceil((label?.offsetWidth ?? 40) + box + 6)
    /* AND THE WIDEST FIGURE BELOW IT (4 Oct 2026), so a drag stops where the
       numbers would start to be cut -- the stored width then matches what the
       reader sees, instead of sitting under a column the cells hold open. */
    return Math.max(header, widestFigure(el))
  }, [])

  return {
    columns: shown as T[],
    all: ordered.map((c) => ({ column: c, hidden: hidden.has(c.key), pinned: (layout.pinned[c.key] ?? null) as Pin })),
    widthOf: (key) => layout.widths[key],
    headerWidth: (key) => els.current.get(key)?.offsetWidth ?? 0,
    offsetOf: (key) => offsets[key] ?? 0,
    pinOf: (key) => (layout.pinned[key] ?? null) as Pin,
    headerRef,
    minWidthOf,
    dirty: !isPristine(layout, base),
    hiddenCount: hiddenHere.length,
    resize: (key, width) =>
      write({ ...layout, widths: { ...layout.widths, [key]: Math.round(width) } }),
    autoFit: (key) => {
      const widths = { ...layout.widths }
      delete widths[key]
      write({ ...layout, widths })
    },
    indexOf: (key) => layout.order.indexOf(key),
    move: (key, toIndex) => {
      const order = layout.order.filter((k) => k !== key)
      order.splice(Math.max(0, Math.min(toIndex, order.length)), 0, key)
      write({ ...layout, order })
    },
    toggle: (key) => {
      const next = new Set(layout.hidden)
      if (next.has(key)) next.delete(key)
      /* Never all of them: an empty grid looks broken rather than configured,
         and there would be no header left to bring one back from. */
      else if (visible.length > 1) next.add(key)
      write({ ...layout, hidden: [...next] })
    },
    hidden: hiddenHere,
    setHidden: (keys) => write({ ...layout, hidden: [...new Set([...keys, ...hiddenDormant])] }),
    setPin: (key, pin) => {
      const pinned = { ...layout.pinned }
      if (pin) pinned[key] = pin
      else delete pinned[key]
      write({ ...layout, pinned })
    },
    /* Reset FORGETS, it does not store the defaults. The stored row is removed
       rather than overwritten with today's arrangement, so a column added next
       year reaches somebody who once pressed this — which is what the button
       says it does. Storing the defaults would freeze the grid as it was on the
       day they pressed it. */
    reset: () => {
      if (gridId) sink?.onForget(gridId)
      write(initial(columns), false)
    },
  }
}

/**
 * The widest FIGURE currently drawn in the column a header heads, padding and
 * border included -- the floor a drag must not cross (4 Oct 2026).
 *
 * MEASURED FROM THE CELLS, because nothing else knows: the width of "SAR
 * 79,869,869" depends on the currency, the data, the font and the type scale,
 * and every grid on the platform draws its own table. So the column is found
 * by POSITION -- the header's starting column, counting spans -- and each body
 * and footer row contributes the cell that starts at that position, if it spans
 * exactly one column (a "Loading…" or "top 500 of N" row spans them all).
 *
 * ONLY CELLS MARKED `num`, which is how every grid here marks a figure. Free
 * text may clip; a figure may not.
 *
 * A Range around the cell's contents gives the width the text actually needs,
 * even while the cell is narrower and the text overflows it -- the cell's own
 * box would only report the width it was given. Run once per drag, on pointer
 * down, so walking a few hundred rows costs nothing anyone can feel.
 */
function widestFigure(th: HTMLElement): number {
  const row = th.parentElement as HTMLTableRowElement | null
  const table = th.closest('table')
  if (!row || !table) return 0
  let start = 0
  for (const c of Array.from(row.cells)) {
    if (c === th) break
    start += c.colSpan
  }
  const range = document.createRange()
  let widest = 0
  let box: number | null = null
  const sections = [...Array.from(table.tBodies), ...(table.tFoot ? [table.tFoot] : [])]
  for (const section of sections) {
    for (const tr of Array.from(section.rows)) {
      let at = 0
      for (const cell of Array.from(tr.cells)) {
        if (at === start) {
          if (cell.colSpan === 1 && cell.classList.contains('num')) {
            if (box === null) {
              const s = getComputedStyle(cell)
              box =
                parseFloat(s.paddingLeft) + parseFloat(s.paddingRight) +
                parseFloat(s.borderLeftWidth) + parseFloat(s.borderRightWidth)
            }
            range.selectNodeContents(cell)
            widest = Math.max(widest, range.getBoundingClientRect().width)
          }
          break
        }
        at += cell.colSpan
        if (at > start) break
      }
    }
  }
  return widest > 0 ? Math.ceil(widest + (box ?? 0) + 1) : 0
}

/**
 * The drag handle on a header's trailing edge, and the whole resize gesture.
 *
 * Pointer events rather than mouse events, so a pen or a touchpad behaves, and
 * setPointerCapture so the drag survives the pointer leaving the 6px strip —
 * which it does immediately, because the column is moving under it.
 */
export function ResizeHandle({
  onResize,
  onAutoFit,
  widthOf,
  minWidth,
  label,
}: {
  onResize: (width: number) => void
  onAutoFit: () => void
  widthOf: () => number
  minWidth: () => number
  label: string
}) {
  const from = useRef<{ x: number; w: number; min: number } | null>(null)
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      title={`Drag to resize ${label}. Double-click to fit.`}
      className="absolute inset-y-0 right-0 z-[2] w-1.5 cursor-col-resize touch-none select-none hover:bg-(--color-accent)/30"
      onDoubleClick={(e) => {
        e.stopPropagation()
        onAutoFit()
      }}
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
        from.current = { x: e.clientX, w: widthOf(), min: minWidth() }
        e.currentTarget.setPointerCapture(e.pointerId)
      }}
      onPointerMove={(e) => {
        const start = from.current
        if (!start) return
        onResize(Math.max(start.min, start.w + (e.clientX - start.x)))
      }}
      onPointerUp={(e) => {
        from.current = null
        e.currentTarget.releasePointerCapture(e.pointerId)
      }}
      onPointerCancel={() => {
        from.current = null
      }}
    />
  )
}

/**
 * "Columns" — the panel that shows, hides, pins and re-orders.
 *
 * Reordering lives here as well as on the header itself, because dragging a
 * header across thirty-four columns means dragging past the edge of the screen
 * and waiting for it to scroll. Moving a row up or down a short list is the
 * same operation without the travel, and it is reachable from the keyboard,
 * which a header drag is not.
 */
/* THE MENU IS PLACED, NOT HUNG (5 Oct 2026). It was `absolute` under its
   button, which lives in a card's header -- so the card's `overflow: hidden`
   sliced it off at the card's bottom edge, and on a short screen the window cut
   it before that: Inventory Aging's drill-down has 21 columns, and the last few
   could not be reached at all. The fix is the one SearchSelect took on 1 Oct
   (ui.tsx, useFloatingPanel): portalled to the body at fixed coordinates from
   the button's own rectangle, opened UPWARD when the room below is short and
   there is more above, capped to the room it opens into, kept on the glass
   sideways, and following the button on scroll and resize. z-[60] for the same
   reason as there: it must clear the full-screen card the button can sit in. */
const COLUMNS_PANEL_WIDTH = 280
const COLUMNS_PANEL_GAP = 4
const COLUMNS_PANEL_MARGIN = 8
/** Never shorter than this, so even a cramped panel shows a handful of rows. */
const COLUMNS_PANEL_MIN = 160
/** The heading plus padding, and one row -- measured in Edge, rounded up. Only
    decides whether to open upward; the cap below is what keeps it on screen. */
const COLUMNS_PANEL_HEAD = 36
const COLUMNS_PANEL_ROW = 24

interface ColumnsPlace { left: number; width: number; top?: number; bottom?: number; maxHeight: number }

export function ColumnsButton({ api }: { api: GridLayoutApi }) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement | null>(null)
  const trigger = useRef<HTMLButtonElement | null>(null)
  const panel = useRef<HTMLDivElement | null>(null)
  const [place, setPlace] = useState<ColumnsPlace | null>(null)
  const rows = api.all.length

  useLayoutEffect(() => {
    if (!open) {
      setPlace(null)
      return
    }
    const measure = () => {
      const el = trigger.current
      if (!el) return
      const r = el.getBoundingClientRect()
      // clientWidth, not innerWidth: a page scrollbar is not room for a panel.
      const vw = document.documentElement.clientWidth
      const vh = window.innerHeight
      const width = Math.min(COLUMNS_PANEL_WIDTH, vw - 2 * COLUMNS_PANEL_MARGIN)
      // Hung from the button's RIGHT edge, as it always was, then kept inside the window.
      const left = Math.max(COLUMNS_PANEL_MARGIN, Math.min(r.right - width, vw - COLUMNS_PANEL_MARGIN - width))
      const cap = Math.round(vh * 0.6)
      const natural = Math.min(cap, COLUMNS_PANEL_HEAD + rows * COLUMNS_PANEL_ROW)
      const below = vh - r.bottom - COLUMNS_PANEL_GAP - COLUMNS_PANEL_MARGIN
      const above = r.top - COLUMNS_PANEL_GAP - COLUMNS_PANEL_MARGIN
      const up = below < natural && above > below
      const room = up ? above : below
      const next: ColumnsPlace = {
        left: Math.round(left),
        width: Math.round(width),
        ...(up ? { bottom: Math.round(vh - r.top + COLUMNS_PANEL_GAP) } : { top: Math.round(r.bottom + COLUMNS_PANEL_GAP) }),
        maxHeight: Math.round(Math.max(COLUMNS_PANEL_MIN, Math.min(cap, room))),
      }
      /* Scrolling the menu's OWN list fires the capture listener too; an
         unchanged answer must not re-render it on every wheel tick. */
      setPlace((p) =>
        p && p.left === next.left && p.width === next.width && p.top === next.top &&
        p.bottom === next.bottom && p.maxHeight === next.maxHeight ? p : next,
      )
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [open, rows])

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      // The menu is portalled, so it is no longer inside `box`: check both.
      const t = e.target as Node
      if (box.current?.contains(t) || panel.current?.contains(t)) return
      setOpen(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  return (
    <div ref={box} className="relative">
      <div className="flex items-center gap-1.5">
        <button
          ref={trigger}
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="rounded border border-(--color-line-strong) bg-(--color-surface) px-2 py-1 text-(length:--text-small) font-medium text-(--color-ink-2) transition-colors hover:bg-(--color-surface-3) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--color-accent)"
        >
          Columns{api.hiddenCount > 0 ? ` · ${api.hiddenCount} hidden` : ''}
        </button>
        {api.dirty && (
          <button
            type="button"
            onClick={api.reset}
            title="Put every column back to its original width, order and position"
            className="rounded px-1.5 py-1 text-(length:--text-small) text-(--color-ink-3) underline underline-offset-2 transition-colors hover:text-(--color-accent)"
          >
            Reset
          </button>
        )}
      </div>

      {/* Above the grid's sticky header (z-30) and the full-screen card (z-40)
          alike; see the note on COLUMNS_PANEL_WIDTH for why it is portalled. */}
      {open && place && createPortal(
        <div
          ref={panel}
          style={{ position: 'fixed', left: place.left, width: place.width, top: place.top, bottom: place.bottom, maxHeight: place.maxHeight }}
          className="z-[60] overflow-auto rounded-md border border-(--color-line) bg-(--color-surface) py-1 shadow-(--shadow-raised)"
        >
          <p className="px-3 pb-1 pt-1.5 text-(length:--text-micro) font-semibold uppercase tracking-[0.06em] text-(--color-ink-3)">
            Shown in this grid
          </p>
          {api.all.map(({ column, hidden, pinned }, i) => (
            <div key={column.key} className="flex items-center gap-1.5 px-2 py-0.5 hover:bg-(--color-surface-3)">
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={!hidden}
                  onChange={() => api.toggle(column.key)}
                  className="shrink-0 accent-(--color-accent)"
                />
                <span className={`truncate text-(length:--text-small) ${hidden ? 'text-(--color-ink-4)' : 'text-(--color-ink-2)'}`}>
                  {column.header}
                </span>
              </label>
              <button
                type="button"
                title={pinned === 'left' ? 'Unpin' : 'Pin to the left'}
                aria-pressed={pinned === 'left'}
                onClick={() => api.setPin(column.key, pinned === 'left' ? null : 'left')}
                className={`rounded px-1 text-(length:--text-micro) ${pinned === 'left' ? 'bg-(--color-navy-100) text-(--color-accent)' : 'text-(--color-ink-4) hover:text-(--color-ink-2)'}`}
              >
                ⇤
              </button>
              <button
                type="button"
                title={pinned === 'right' ? 'Unpin' : 'Pin to the right'}
                aria-pressed={pinned === 'right'}
                onClick={() => api.setPin(column.key, pinned === 'right' ? null : 'right')}
                className={`rounded px-1 text-(length:--text-micro) ${pinned === 'right' ? 'bg-(--color-navy-100) text-(--color-accent)' : 'text-(--color-ink-4) hover:text-(--color-ink-2)'}`}
              >
                ⇥
              </button>
              {/* By the NEIGHBOUR's place in the full order, as a header drag
                  does, not by position in this list: the order also holds
                  dormant columns this grid is not drawing, and "one up" by list
                  position could land the column next to one of those -- a click
                  that visibly does nothing. */}
              <button
                type="button"
                title="Move up"
                disabled={i === 0}
                onClick={() => {
                  const prev = api.all[i - 1]
                  if (prev) api.move(column.key, api.indexOf(prev.column.key))
                }}
                className="rounded px-1 text-(length:--text-micro) text-(--color-ink-4) transition-colors hover:text-(--color-ink-2) disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button"
                title="Move down"
                disabled={i === api.all.length - 1}
                onClick={() => {
                  const next = api.all[i + 1]
                  if (next) api.move(column.key, api.indexOf(next.column.key))
                }}
                className="rounded px-1 text-(length:--text-micro) text-(--color-ink-4) transition-colors hover:text-(--color-ink-2) disabled:opacity-30"
              >
                ↓
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  )
}

/**
 * WHERE THE COLUMNS BUTTON GOES, AND WHY IT IS A SLOT.
 *
 * It began on a strip of its own above the grid, which cost a band of height
 * across every report for one button — and height is the scarce axis on the
 * 1536x864 screen most of this platform is read on. The owner asked for it
 * beside Full screen, in the row the extract buttons already occupy, which
 * costs nothing.
 *
 * A slot rather than a prop because of who knows what. The card header renders
 * ABOVE the grid and knows nothing about its columns; the layout belongs to the
 * grid, which renders below. The card publishes an element here, the grid
 * portals its control into it, and neither has to hold the other's state. A
 * grid with no card around it finds no slot and keeps the strip.
 *
 * It also survives going full screen: the card moves its header into the
 * overlay's top bar, the ref fires again with the new element, and the control
 * follows it.
 */
export const GridChromeSlot = createContext<HTMLElement | null>(null)

/** A slim bar above a grid — the fallback when no card offers a slot. */
export function GridToolbar({ api, children }: { api: GridLayoutApi; children?: ReactNode }) {
  return (
    <div className="no-print mb-1 flex items-center justify-end gap-2">
      {children}
      <ColumnsButton api={api} />
    </div>
  )
}

/**
 * Put the grid's controls wherever they belong: a card's header slot when there
 * is one, otherwise a strip of its own above the grid.
 */
export function GridChrome({ api }: { api: GridLayoutApi }) {
  const slot = useContext(GridChromeSlot)
  if (slot) return createPortal(<ColumnsButton api={api} />, slot)
  return <GridToolbar api={api} />
}
